import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, Plus, ArrowLeft, Trash2, GraduationCap, BookOpen, ClipboardList,
  CalendarClock, CheckCircle2, Clock, AlertTriangle, UserPlus, Award, LineChart, Trophy, Presentation,
} from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return iso; } };

const STATUS = {
  done: { label: "Done", cls: "text-success border-success/40", icon: CheckCircle2 },
  pending: { label: "Pending", cls: "text-muted-foreground", icon: Clock },
  overdue: { label: "Overdue", cls: "text-red-500 border-red-500/40", icon: AlertTriangle },
};

function StatusChip({ status }) {
  const s = STATUS[status] || STATUS.pending;
  const Icon = s.icon;
  return <Badge variant="outline" className={`gap-1 ${s.cls}`}><Icon className="h-3 w-3" />{s.label}</Badge>;
}

export default function Instructor() {
  const { user } = useAuth();
  const [cohorts, setCohorts] = useState(null);
  const [detail, setDetail] = useState(null);
  const [aza, setAza] = useState(null);
  const [lb, setLb] = useState(null);
  const [catalog, setCatalog] = useState({ labs: [], quizzes: [] });
  const [pool, setPool] = useState([]);

  // dialogs
  const [showCreate, setShowCreate] = useState(false);
  const [newCohort, setNewCohort] = useState({ name: "", description: "" });
  const [showAddStudents, setShowAddStudents] = useState(false);
  const [selStudents, setSelStudents] = useState({});
  const [showAssign, setShowAssign] = useState(false);
  const [asg, setAsg] = useState({ type: "lab", ref_id: "", due_at: "" });
  const [confirmDelCohort, setConfirmDelCohort] = useState(null);

  const loadCohorts = useCallback(() => api.get("/instructor/cohorts").then((r) => setCohorts(r.data.cohorts)).catch((e) => toast.error(apiErr(e))), []);
  useEffect(() => { loadCohorts(); }, [loadCohorts]);

  if (user && !["instructor", "admin"].includes(user.role)) return <Navigate to="/dashboard" replace />;

  const openCohort = async (id) => {
    try {
      const { data } = await api.get(`/instructor/cohorts/${id}`); setDetail(data);
      setAza(null); setLb(null);
      api.get(`/instructor/cohorts/${id}/azure-analytics`).then((r) => setAza(r.data)).catch(() => {});
      api.get(`/instructor/cohorts/${id}/leaderboard`).then((r) => setLb(r.data)).catch(() => {});
    }
    catch (e) { toast.error(apiErr(e)); }
  };
  const refreshDetail = () => detail && openCohort(detail.id);

  const createCohort = async () => {
    if (!newCohort.name.trim()) return toast.error("Enter a cohort name");
    try { await api.post("/instructor/cohorts", newCohort); toast.success("Cohort created"); setShowCreate(false); setNewCohort({ name: "", description: "" }); loadCohorts(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const deleteCohort = async (id) => {
    try { await api.delete(`/instructor/cohorts/${id}`); toast.success("Cohort deleted"); setConfirmDelCohort(null); setDetail(null); loadCohorts(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const openAddStudents = async () => {
    try { const { data } = await api.get("/instructor/students"); setPool(data.students); setSelStudents({}); setShowAddStudents(true); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const addStudents = async () => {
    const ids = Object.keys(selStudents).filter((k) => selStudents[k]);
    if (!ids.length) return toast.error("Select at least one student");
    try { await api.post(`/instructor/cohorts/${detail.id}/students`, { student_ids: ids }); toast.success(`Added ${ids.length} student(s)`); setShowAddStudents(false); refreshDetail(); loadCohorts(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const removeStudent = async (sid) => {
    try { await api.delete(`/instructor/cohorts/${detail.id}/students/${sid}`); refreshDetail(); loadCohorts(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const openAssign = async () => {
    if (!catalog.labs.length) { try { const { data } = await api.get("/instructor/catalog"); setCatalog(data); } catch (e) {} }
    setAsg({ type: "lab", ref_id: "", due_at: "" });
    setShowAssign(true);
  };
  const createAssignment = async () => {
    if (!asg.ref_id) return toast.error("Pick a lab or quiz");
    try { await api.post(`/instructor/cohorts/${detail.id}/assignments`, asg); toast.success("Assignment added"); setShowAssign(false); refreshDetail(); loadCohorts(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const deleteAssignment = async (aid) => {
    try { await api.delete(`/instructor/assignments/${aid}`); refreshDetail(); loadCohorts(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const options = asg.type === "lab" ? catalog.labs : catalog.quizzes;
  const inCohortIds = new Set((detail?.students || []).map((s) => s.id));

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="instructor-page">
      {!detail ? (
        <>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Users className="h-6 w-6 text-primary" />
              <div>
                <h1 className="font-display font-bold text-3xl tracking-tight">Instructor Portal</h1>
                <p className="text-muted-foreground text-sm mt-1">Create cohorts, assign labs & quizzes, and track student progress.</p>
              </div>
            </div>
            <div className="ml-auto flex gap-2">
              <a href="/admin/lecturer" data-testid="nav-virtual-lecturer"><Button variant="outline" className="gap-2"><Presentation className="h-4 w-4" /> Virtual Lecturer</Button></a>
              <Button className="gap-2" onClick={() => setShowCreate(true)} data-testid="new-cohort-btn"><Plus className="h-4 w-4" /> New Cohort</Button>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            {cohorts?.length === 0 && (
              <div className="col-span-full rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="no-cohorts">
                No cohorts yet. Create your first cohort to start assigning work.
              </div>
            )}
            {cohorts?.map((c) => (
              <button key={c.id} onClick={() => openCohort(c.id)} data-testid={`cohort-card-${c.id}`}
                className="text-left rounded-xl border border-border bg-card p-5 hover:border-primary/40 hover:shadow-sm transition-all">
                <div className="font-display font-bold text-lg">{c.name}</div>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2 min-h-[40px]">{c.description || "No description"}</p>
                <div className="flex gap-4 mt-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5"><Users className="h-4 w-4" />{c.student_count}</span>
                  <span className="flex items-center gap-1.5"><ClipboardList className="h-4 w-4" />{c.assignment_count}</span>
                </div>
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <button onClick={() => setDetail(null)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="cohort-back-btn">
            <ArrowLeft className="h-4 w-4" /> All cohorts
          </button>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-display font-bold text-3xl tracking-tight">{detail.name}</h1>
              <p className="text-muted-foreground text-sm mt-1">{detail.description || "No description"}</p>
            </div>
            <Button variant="outline" className="gap-2 text-red-500 hover:text-red-500" onClick={() => setConfirmDelCohort(detail)} data-testid="delete-cohort-btn">
              <Trash2 className="h-4 w-4" /> Delete cohort
            </Button>
          </div>

          <Tabs defaultValue="progress" className="mt-6">
            <TabsList>
              <TabsTrigger value="progress" data-testid="tab-progress"><GraduationCap className="h-4 w-4 mr-1.5" />Progress</TabsTrigger>
              <TabsTrigger value="students" data-testid="tab-students"><Users className="h-4 w-4 mr-1.5" />Students ({detail.students.length})</TabsTrigger>
              <TabsTrigger value="assignments" data-testid="tab-assignments"><ClipboardList className="h-4 w-4 mr-1.5" />Assignments ({detail.assignments.length})</TabsTrigger>
              <TabsTrigger value="azure" data-testid="tab-azure-analytics"><LineChart className="h-4 w-4 mr-1.5" />AZ-104 Analytics</TabsTrigger>
              <TabsTrigger value="leaderboard" data-testid="tab-leaderboard"><Trophy className="h-4 w-4 mr-1.5" />Leaderboard</TabsTrigger>
            </TabsList>

            {/* Progress matrix */}
            <TabsContent value="progress" className="mt-4">
              {detail.students.length === 0 ? (
                <div className="rounded-xl border border-dashed p-10 text-center text-muted-foreground">Add students to see progress.</div>
              ) : (
                <div className="rounded-lg border border-border bg-card overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-secondary/50 text-left">
                        <th className="p-3 sticky left-0 bg-secondary/50">Student</th>
                        <th className="p-3">Overall</th>
                        <th className="p-3">Assignments</th>
                        {detail.assignments.map((a) => (
                          <th key={a.id} className="p-3 whitespace-nowrap text-xs">{a.title}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {detail.students.map((s) => (
                        <tr key={s.id} className="border-t border-border" data-testid={`progress-row-${s.id}`}>
                          <td className="p-3 sticky left-0 bg-card"><div className="font-medium">{s.name}</div><div className="text-xs text-muted-foreground">{s.email}</div></td>
                          <td className="p-3"><div className="flex items-center gap-2"><Progress value={s.completion_pct} className="h-1.5 w-16" /><span className="text-xs text-muted-foreground">{s.completion_pct}%</span></div></td>
                          <td className="p-3 font-mono text-xs">{s.assignments_done}/{s.assignments_total}</td>
                          {detail.assignments.map((a) => (
                            <td key={a.id} className="p-3"><StatusChip status={s.assignment_status[a.id]?.status || "pending"} /></td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            {/* Students */}
            <TabsContent value="students" className="mt-4">
              <div className="flex justify-end mb-3">
                <Button className="gap-2" onClick={openAddStudents} data-testid="add-students-btn"><UserPlus className="h-4 w-4" /> Add students</Button>
              </div>
              <div className="rounded-lg border border-border bg-card overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="bg-secondary/50 text-left"><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">XP</th><th className="p-3 text-right">Remove</th></tr></thead>
                  <tbody>
                    {detail.students.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">No students in this cohort.</td></tr>}
                    {detail.students.map((s) => (
                      <tr key={s.id} className="border-t border-border" data-testid={`student-row-${s.id}`}>
                        <td className="p-3 font-medium">{s.name}</td>
                        <td className="p-3 text-muted-foreground">{s.email}</td>
                        <td className="p-3 font-mono">{s.xp}</td>
                        <td className="p-3 text-right"><Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => removeStudent(s.id)} data-testid={`remove-student-${s.id}`}><Trash2 className="h-4 w-4" /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* Assignments */}
            <TabsContent value="assignments" className="mt-4">
              <div className="flex justify-end mb-3">
                <Button className="gap-2" onClick={openAssign} data-testid="new-assignment-btn"><Plus className="h-4 w-4" /> New assignment</Button>
              </div>
              <div className="space-y-2">
                {detail.assignments.length === 0 && <div className="rounded-xl border border-dashed p-10 text-center text-muted-foreground">No assignments yet.</div>}
                {detail.assignments.map((a) => (
                  <div key={a.id} className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3" data-testid={`assignment-${a.id}`}>
                    <div className="flex items-center gap-3">
                      {a.type === "quiz" ? <GraduationCap className="h-5 w-5 text-primary" /> : <BookOpen className="h-5 w-5 text-primary" />}
                      <div>
                        <div className="font-medium">{a.title}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-2">
                          <Badge variant="secondary" className="uppercase text-[10px]">{a.type}</Badge>
                          <span className="flex items-center gap-1"><CalendarClock className="h-3 w-3" /> Due {fmtDate(a.due_at)}</span>
                        </div>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => deleteAssignment(a.id)} data-testid={`delete-assignment-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* AZ-104 Analytics */}
            <TabsContent value="azure" className="mt-4" data-testid="azure-analytics-panel">
              {!aza ? (
                <div className="rounded-xl border border-dashed p-10 text-center text-muted-foreground">Loading AZ-104 analytics…</div>
              ) : aza.students.length === 0 ? (
                <div className="rounded-xl border border-dashed p-10 text-center text-muted-foreground">Add students to see their Azure Academy progress.</div>
              ) : (
                <>
                  <div className="text-sm text-muted-foreground mb-3">Per-student progress across the {aza.total_labs}-lab Azure Cloud Academy, broken down by track, plus the Practical Exam and certificate status.</div>
                  <div className="rounded-lg border border-border bg-card overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-secondary/50 text-left">
                          <th className="p-3 sticky left-0 bg-secondary/50">Student</th>
                          <th className="p-3 whitespace-nowrap">Overall</th>
                          {aza.tracks.map((t) => (
                            <th key={t.key} className="p-3 text-center whitespace-nowrap">{t.name}</th>
                          ))}
                          <th className="p-3 whitespace-nowrap">Exam</th>
                          <th className="p-3 whitespace-nowrap">Certificate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {aza.students.map((s) => (
                          <tr key={s.id} className="border-t border-border" data-testid={`az-analytics-row-${s.id}`}>
                            <td className="p-3 sticky left-0 bg-card"><div className="font-medium">{s.name}</div><div className="text-xs text-muted-foreground">{s.email}</div></td>
                            <td className="p-3"><div className="flex items-center gap-2"><Progress value={s.azure_pct} className="h-1.5 w-16" /><span className="text-xs text-muted-foreground whitespace-nowrap">{s.labs_done}/{s.labs_total}</span></div></td>
                            {aza.tracks.map((t) => {
                              const tp = s.track_progress[t.key] || { pct: 0, completed: 0, total: 0, earned: false };
                              return (
                                <td key={t.key} className="p-3 text-center" data-testid={`az-cell-${s.id}-${t.key}`}>
                                  <div className={`inline-flex flex-col items-center ${tp.earned ? "text-amber-600" : ""}`}>
                                    <span className="text-xs font-mono">{tp.completed}/{tp.total}</span>
                                    <div className="h-1 w-12 rounded-full bg-muted mt-1 overflow-hidden"><div className={`h-full ${tp.earned ? "bg-amber-500" : "bg-[#0078d4]"}`} style={{ width: `${tp.pct}%` }} /></div>
                                  </div>
                                </td>
                              );
                            })}
                            <td className="p-3">
                              {s.exam_passed
                                ? <Badge variant="outline" className="gap-1 text-green-600 border-green-500/40"><Award className="h-3 w-3" />{s.exam_score}%</Badge>
                                : s.exam_attempts > 0
                                  ? <span className="text-xs text-muted-foreground">{s.exam_score}%</span>
                                  : <span className="text-xs text-muted-foreground">—</span>}
                            </td>
                            <td className="p-3">
                              {s.certificate_eligible
                                ? <Badge variant="outline" className="gap-1 text-[#0078d4] border-[#0078d4]/40"><CheckCircle2 className="h-3 w-3" />Earned</Badge>
                                : <span className="text-xs text-muted-foreground">In progress</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3 text-xs text-muted-foreground">Each track cell shows labs completed / total. Amber = track badge earned. Certificate unlocks at Foundations + Core complete and 80% overall.</div>
                </>
              )}
            </TabsContent>

            {/* Leaderboard */}
            <TabsContent value="leaderboard" className="mt-4" data-testid="leaderboard-panel">
              {!lb ? (
                <div className="rounded-xl border border-dashed p-10 text-center text-muted-foreground">Loading leaderboard…</div>
              ) : lb.entries.length === 0 ? (
                <div className="rounded-xl border border-dashed p-10 text-center text-muted-foreground">Add students to see the class leaderboard.</div>
              ) : (
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground mb-1">Ranked by labs completed, then track badges, then Practical Exam score.</div>
                  {lb.entries.map((e) => (
                    <div key={e.id} className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-2.5" data-testid={`lb-entry-${e.rank}`}>
                      <span className={`h-8 w-8 rounded-full grid place-items-center text-sm font-bold ${e.rank === 1 ? "bg-amber-400 text-amber-950" : e.rank === 2 ? "bg-slate-300 text-slate-800" : e.rank === 3 ? "bg-orange-400 text-orange-950" : "bg-muted text-muted-foreground"}`}>{e.rank}</span>
                      <div className="min-w-0 flex-1"><div className="font-medium truncate">{e.name}</div><div className="text-xs text-muted-foreground">{e.labs_completed}/{e.labs_total} labs · {e.badges} badges{e.exam_passed ? ` · exam ${e.exam_score}%` : ""}</div></div>
                      {e.certificate && <Badge variant="outline" className="gap-1 text-[#0078d4] border-[#0078d4]/40"><CheckCircle2 className="h-3 w-3" />Certified</Badge>}
                      <span className="text-lg font-bold text-muted-foreground w-14 text-right">{e.overall_pct}%</span>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

          </Tabs>
        </>
      )}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-cohort-dialog">
          <DialogHeader><DialogTitle className="font-display">New Cohort</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Cohort name (e.g. Fall 2026 — Linux Basics)" value={newCohort.name} onChange={(e) => setNewCohort((c) => ({ ...c, name: e.target.value }))} data-testid="cohort-name-input" />
            <Textarea placeholder="Description (optional)" value={newCohort.description} onChange={(e) => setNewCohort((c) => ({ ...c, description: e.target.value }))} data-testid="cohort-desc-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createCohort} data-testid="save-cohort-btn">Create cohort</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add students dialog */}
      <Dialog open={showAddStudents} onOpenChange={setShowAddStudents}>
        <DialogContent data-testid="add-students-dialog">
          <DialogHeader><DialogTitle className="font-display">Add students to {detail?.name}</DialogTitle></DialogHeader>
          <div className="max-h-80 overflow-auto space-y-1">
            {pool.filter((p) => !inCohortIds.has(p.id)).length === 0 && <div className="text-sm text-muted-foreground p-4 text-center">All students are already in this cohort.</div>}
            {pool.filter((p) => !inCohortIds.has(p.id)).map((p) => (
              <label key={p.id} className="flex items-center gap-3 p-2 rounded hover:bg-accent cursor-pointer" data-testid={`pool-student-${p.id}`}>
                <Checkbox checked={!!selStudents[p.id]} onCheckedChange={(v) => setSelStudents((s) => ({ ...s, [p.id]: v }))} />
                <div><div className="text-sm font-medium">{p.name}</div><div className="text-xs text-muted-foreground">{p.email}</div></div>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddStudents(false)}>Cancel</Button>
            <Button onClick={addStudents} data-testid="confirm-add-students-btn">Add selected</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New assignment dialog */}
      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent data-testid="assignment-dialog">
          <DialogHeader><DialogTitle className="font-display">New Assignment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Type</label>
              <div className="flex gap-2 mt-1">
                {["lab", "quiz"].map((t) => (
                  <Button key={t} type="button" variant={asg.type === t ? "default" : "outline"} size="sm" className="capitalize" onClick={() => setAsg((a) => ({ ...a, type: t, ref_id: "" }))} data-testid={`asg-type-${t}`}>{t}</Button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">{asg.type === "lab" ? "Lab" : "Quiz"}</label>
              <select className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm" value={asg.ref_id} onChange={(e) => setAsg((a) => ({ ...a, ref_id: e.target.value }))} data-testid="asg-ref-select">
                <option value="">Select a {asg.type}…</option>
                {options.map((o) => (<option key={o.id} value={o.id}>{`L${o.level} · ${o.title}`}</option>))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Due date (optional)</label>
              <Input type="date" value={asg.due_at} onChange={(e) => setAsg((a) => ({ ...a, due_at: e.target.value }))} data-testid="asg-due-input" className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssign(false)}>Cancel</Button>
            <Button onClick={createAssignment} data-testid="save-assignment-btn">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete cohort confirm */}
      <AlertDialog open={!!confirmDelCohort} onOpenChange={(o) => !o && setConfirmDelCohort(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{confirmDelCohort?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>This removes the cohort and all its assignments. Student progress and accounts are not affected.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteCohort(confirmDelCohort.id)} data-testid="confirm-delete-cohort">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
